import React, { useEffect, useState } from 'react'
import firebase from 'firebase/app'
import 'firebase/firestore'

import './TelemetryImports.css'

const formatLapTime = value => {
  if (!Number.isFinite(Number(value))) return '—'
  const seconds = Number(value)
  const minutes = Math.floor(seconds / 60)
  const remainder = (seconds % 60).toFixed(3).padStart(6, '0')
  return minutes ? `${minutes}:${remainder}` : remainder
}

const formatDate = value => {
  const date = value?.toDate ? value.toDate() : value ? new Date(value) : null
  if (!date || Number.isNaN(date.getTime())) return 'Recent session'
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

const topSpeed = report => {
  const value = Number(report?.summary?.maxSpeed)
  if (!Number.isFinite(value)) return '—'
  return `${Math.round(report?.units?.speed === 'km/h' ? value : value * 3.6)} km/h`
}

const RacingLinePreview = ({ points = [], label }) => {
  const valid = points.filter(point => point?.lat != null && point?.lng != null && Number.isFinite(Number(point.lat)) && Number.isFinite(Number(point.lng)))
  if (valid.length < 2) return <div className="telemetry-session-card__line-placeholder">Measured line will appear here</div>
  const width = 560
  const height = 260
  const padding = 30
  const latitudes = valid.map(point => Number(point.lat))
  const longitudes = valid.map(point => Number(point.lng))
  const minLat = Math.min(...latitudes)
  const maxLat = Math.max(...latitudes)
  const minLng = Math.min(...longitudes)
  const maxLng = Math.max(...longitudes)
  const latRange = maxLat - minLat || 1
  const lngRange = maxLng - minLng || 1
  const scale = Math.min((width - padding * 2) / lngRange, (height - padding * 2) / latRange)
  const drawnWidth = lngRange * scale
  const drawnHeight = latRange * scale
  const offsetX = (width - drawnWidth) / 2
  const offsetY = (height - drawnHeight) / 2
  const coordinates = valid.map(point => ({
    x: offsetX + (Number(point.lng) - minLng) * scale,
    y: height - offsetY - (Number(point.lat) - minLat) * scale,
    type: point.type,
  }))
  const path = coordinates.map(point => `${point.x},${point.y}`).join(' ')
  const color = type => type === 'brake' ? '#ff5a67' : type === 'acc' ? '#60e89a' : '#ffd166'

  return (
    <svg className="telemetry-session-card__line" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${label} measured racing line`}>
      <defs>
        <pattern id="telemetry-card-grid" width="28" height="28" patternUnits="userSpaceOnUse"><path d="M 28 0 L 0 0 0 28" fill="none" stroke="rgba(255,255,255,.055)" strokeWidth="1" /></pattern>
      </defs>
      <rect width={width} height={height} fill="url(#telemetry-card-grid)" />
      <polyline points={path} fill="none" stroke="rgba(255,255,255,.17)" strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" />
      {coordinates.slice(1).map((point, index) => <line key={`${point.x}-${point.y}`} x1={coordinates[index].x} y1={coordinates[index].y} x2={point.x} y2={point.y} stroke={color(point.type)} strokeWidth="6" strokeLinecap="round" />)}
      <circle cx={coordinates[0].x} cy={coordinates[0].y} r="6" fill="#fff" />
    </svg>
  )
}

const SessionCard = ({ item }) => {
  const report = item.derivedReport || {}
  const summary = report.summary || {}
  const isReady = item.status === 'ready'
  const title = report.track?.name || item.trackInfo?.name || 'Telemetry session'
  const subtitle = [report.track?.configuration, report.vehicle?.name].filter(Boolean).join(' · ')
  const statusLabel = item.status === 'failed' ? 'Needs attention' : 'Processing'

  return (
    <article className={`telemetry-session-card${isReady ? '' : ' is-pending'}`}>
      <div className="telemetry-session-card__visual">
        <RacingLinePreview points={report.linePreview} label={title} />
        <span className="telemetry-session-card__source">{String(item.source || 'telemetry').toUpperCase()}</span>
      </div>
      <div className="telemetry-session-card__body">
        <div className="telemetry-session-card__heading">
          <div>
            <p>{formatDate(item.timestamp || item.createdAt)}</p>
            <h3>{title}</h3>
            {subtitle && <span>{subtitle}</span>}
          </div>
          {!isReady && <strong className="telemetry-session-card__status">{statusLabel}</strong>}
        </div>
        <dl className="telemetry-session-card__metrics">
          <div><dt>Laps</dt><dd>{summary.totalLapCount ?? '—'}</dd></div>
          <div><dt>Best lap</dt><dd>{formatLapTime(summary.bestLapSeconds)}</dd></div>
          <div><dt>Top speed</dt><dd>{topSpeed(report)}</dd></div>
        </dl>
        {isReady
          ? <a className="telemetry-session-card__action" href={`/telemetry-report/?id=${encodeURIComponent(item.id)}`}>Open report</a>
          : <p className="telemetry-session-card__message">{item.status === 'failed' ? 'Your original upload is safe. Try importing it again.' : 'Your report is being prepared. You can come back later.'}</p>}
      </div>
    </article>
  )
}

const TelemetryImports = ({ user }) => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false)
      return undefined
    }
    setLoading(true)
    return firebase.firestore().collection('telemetry')
      .where('userId', '==', user.uid)
      .orderBy('timestamp', 'desc').limit(12)
      .onSnapshot(snapshot => {
        const sessions = snapshot.docs
          .map(document => ({ id: document.id, ...document.data() }))
        setItems(sessions)
        setLoading(false)
        setError(null)
      }, snapshotError => {
        console.error('Could not load telemetry imports:', snapshotError)
        setLoading(false)
        setError('Your saved sessions could not be loaded right now.')
      })
  }, [user?.uid])

  return (
    <section className="telemetry-imports">
      <header className="telemetry-imports__header">
        <div><p>Telemetry</p><h2>Your measured sessions</h2></div>
        <a href="/alfano-racing-line-importer/">Import a session</a>
      </header>
      {loading && <p className="telemetry-imports__empty" role="status">Loading your measured sessions…</p>}
      {!loading && error && <p className="telemetry-imports__empty">{error}</p>}
      {!loading && !error && items.length === 0 && <p className="telemetry-imports__empty">Your ALFANO and iRacing sessions will appear here after import.</p>}
      <div className="telemetry-imports__grid">
        {items.map(item => <SessionCard item={item} key={item.id} />)}
      </div>
    </section>
  )
}

export default TelemetryImports
